import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";

import { cn } from "./cn";
import IconButton from "./IconButton";

type Size = "sm" | "md" | "lg";

type Props = {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: Size;
  labelledBy?: string;
  describedBy?: string;
  initialFocusRef?: React.RefObject<HTMLElement>;
  closeOnBackdrop?: boolean;
  showClose?: boolean;
};

const sizes: Record<Size, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
};

const Modal: React.FC<Props> = ({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  labelledBy,
  describedBy,
  initialFocusRef,
  closeOnBackdrop = true,
  showClose = true,
}) => {
  const [mounted, setMounted] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => {
      cancelAnimationFrame(raf);
      setVisible(false);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const raf = requestAnimationFrame(() => {
      (initialFocusRef?.current ?? dialogRef.current)?.focus();
    });

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      cancelAnimationFrame(raf);
    };
  }, [open, onClose, initialFocusRef]);

  if (!mounted || !open) return null;

  const autoTitleId = title ? "modal-title" : undefined;
  const autoDescId = description ? "modal-description" : undefined;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999999]"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy ?? autoTitleId}
      aria-describedby={describedBy ?? autoDescId}
      onMouseDown={(e) => {
        if (!closeOnBackdrop) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          "absolute inset-0 bg-gray-900/40 backdrop-blur-[2px] transition-opacity duration-200",
          visible ? "opacity-100" : "opacity-0",
        )}
      />

      <div
        className="relative z-10 flex min-h-full items-center justify-center p-4"
        onMouseDown={(e) => {
          if (!closeOnBackdrop) return;
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          ref={dialogRef}
          tabIndex={-1}
          onMouseDown={(e) => e.stopPropagation()}
          className={cn(
            "relative w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_20px_60px_-20px_rgba(16,24,40,0.25)] outline-none",
            "transition-[opacity,transform] duration-200 ease-out",
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
            sizes[size],
          )}
        >
          {(title || showClose) && (
            <div className="flex items-start justify-between gap-4 px-6 pt-5">
              <div className="min-w-0 flex-1">
                {title ? (
                  <h2
                    id={autoTitleId}
                    className="text-[17px] font-semibold tracking-tight text-gray-900"
                  >
                    {title}
                  </h2>
                ) : null}
                {description ? (
                  <p
                    id={autoDescId}
                    className="mt-1.5 text-[13px] leading-relaxed text-gray-600"
                  >
                    {description}
                  </p>
                ) : null}
              </div>
              {showClose ? (
                <IconButton
                  aria-label="Close dialog"
                  onClick={onClose}
                  variant="ghost"
                  size="sm"
                  className="-mr-1.5 -mt-1"
                >
                  <FaTimes className="h-3.5 w-3.5" />
                </IconButton>
              ) : null}
            </div>
          )}

          {children ? <div className="px-6 pt-4">{children}</div> : null}

          {footer ? (
            <div className="mt-5 flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50/60 px-6 py-4">
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
